import { DataGrid } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomGridToolbar from '../../datagrid/GridToolBar';
import GlobalAnchor from '../GlobalAnchor/GlobalAnchor';
import { Box, Button } from '@mui/material';
import useResponsive from '../../../Hooks/useResponsive';
import { useDispatch } from 'react-redux';
import { clearPaginationData, savePaginationData } from '../../../redux/features/globalDatagridSlice';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Loader3D from '../../Loader/Loader3D';

const GlobalDatagrid = ({
  loading,
  tableHead,
  rows,
  rowUniqueId,
  getRowHeight,
  meta,
  actionComponent,
  rowClick,
  checkboxSelection,
  headerHeight,
  setCurrentRowDetails,
  disableSelectionOnClick = true,
}) => {
  const location = useLocation();
  const smUp = useResponsive('up', 'sm');

  const mdUp = useResponsive('up', 'md');
  const dispatch = useDispatch();
  // ------------------------------------------------------------------------------
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const [gridSize, setGridSize] = useState(paginationData?.gridSize || 10);
  const [pageNum, setPageNum] = useState(paginationData?.pageNum || 1);
  const [rowCount, setRowCount] = useState(meta || 0);
  const [rowData, setRowData] = useState('');
  const [search, setSearch] = useState(paginationData?.search || '');
  const [openAnchor, setOpenAnchor] = useState(paginationData?.openAnchor || false);
  const [pathName, setPathName] = useState('');
  const [checkedRowsIds, setCheckedRowsIds] = useState([]);

  const [anchorLoading, setAnchorLoading] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(0);
  const memoizedRows = useMemo(() => rows, [rows]);
  const [sortModel, setSortModel] = useState(paginationData?.sortModel || { field: '', order: '' });

  useEffect(() => {
    if (openAnchor) {
      setAnchorLoading(true);
      setTimeout(() => {
        setAnchorLoading(false);
      }, 1000);
    }
  }, [openAnchor]);

  useEffect(() => {
    if (location.pathname) {
      setPathName(location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    dispatch(clearPaginationData());
  }, [pathName]);

  useEffect(() => {
    if (paginationData) {
      const { pageNum, gridSize, openAnchor, search, checkedRowsIds, sortModel } = paginationData;

      setPageNum((prev) => (prev !== pageNum ? pageNum : prev));
      setGridSize((prev) => (prev !== gridSize ? gridSize : prev));
      setOpenAnchor((prev) => (prev !== openAnchor ? openAnchor : prev));
      setSearch((prev) => (prev !== search ? search : prev));
      setCheckedRowsIds((prev) => (prev !== checkedRowsIds ? checkedRowsIds : prev));
      setSortModel((prev) => (prev !== sortModel ? sortModel : prev));
    }
  }, [paginationData]);
  const paginationDatas = useMemo(() => {
    return {
      gridSize,
      pageNum,
      rowCount,
      rowData,
      search,
      checkedRowsIds,
      openAnchor: false,
      sortModel,
    };
  }, [gridSize, pageNum, rowCount, rowData, search, checkedRowsIds, sortModel]);

  useEffect(() => {
    setRowCount(meta || 0);
  }, [rows]);
  // ----------------------------------------------------------------------------------

  useEffect(() => {
    // Combine all relevant data into one object for dispatch
    // const paginationData = {
    //   gridSize,
    //   pageNum,
    //   rowCount,
    //   rowData,
    //   openAnchor,
    //   search,
    //   checkedRowsIds,
    // };

    dispatch(savePaginationData(paginationDatas));
  }, [paginationDatas]);

  const handleSortModelChange = (sortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0]; // Field and sort direction (asc/desc)
      setSortModel({ field, order: sort });
    } else {
      setSortModel({ field: '', order: '' });
    }
  };

  const previousSearchValue = useRef('');
  const handleFilterModelChange = (filterModel) => {
    // const quickFilter = filterModel.quickFilterValues?.[0] || "";
    const quickFilter = filterModel.quickFilterValues ? filterModel.quickFilterValues.join(' ') : '';

    if (quickFilter !== previousSearchValue.current) {
      searchFunction(quickFilter);
      previousSearchValue.current = quickFilter;
    }
    if (filterModel.items.length > 0) {
      filterFunction(filterModel.items);
    }
  };

  // const searchFunction = (searchValue) => {
  //   setSearch(searchValue);
  // };
  let searchTimeout;
  const searchFunction = (searchValue) => {
    clearTimeout(searchTimeout); // Clear any existing timeout
    searchTimeout = setTimeout(() => {
      setSearch(searchValue);
    }, 1000);
  };

  const filterFunction = (filters) => {
    filters.forEach((filter) => {
      const { columnField, operatorValue, value } = filter;
    });
  };
  useEffect(() => {
    if (!openAnchor) {
      setRowData('');
    }
  }, [openAnchor]);

  useEffect(() => {
    if (rowData) {
      // setOpenAnchor(true);
      dispatch(savePaginationData({ ...paginationDatas, openAnchor: true }));
    } else {
      // setOpenAnchor(false);
      savePaginationData({ ...paginationDatas, openAnchor: false });
    }
  }, [rowData]);

  const handleClick = useCallback(() => {
    setOpenAnchor(false);
    savePaginationData(paginationDatas);
  }, [setOpenAnchor, savePaginationData, paginationDatas]);

  const handleSelectionChange = useCallback(
    (datas) => {
      if (checkboxSelection) {
        setCheckedRowsIds(datas);
      }
    },
    [setCheckedRowsIds],
  );

  const handleDubleClick = useCallback(
    (e) => {
      const rowData = rows?.find((row) => row[rowUniqueId] === parseInt(e.id, 10));
      if (rowClick && rowData) {
        setRowData(rowData);
      }
    },
    [rows, rowUniqueId, rowClick, setRowData],
  );

  const handleRowClick = useCallback(
    (e) => {
      if (setCurrentRowDetails) {
        setCurrentRowDetails(e?.row);
      }
      if (!smUp) {
        const rowData = rows.find((row) => row[rowUniqueId] === parseInt(e.id, 10));

        if (rowData && rowClick) {
          const currentTime = Date.now();

          if (currentTime - lastClickTime < 300) {
            setRowData(rowData);
          }
          setLastClickTime(currentTime);
        }
      }
    },
    [smUp, rows, rowUniqueId, rowClick, lastClickTime, setRowData],
  );

  ////....................COLUMN VISIBILITY SECTION......//
  const columnInitialVisibility = useMemo(() => {
    if (tableHead?.length > 0) {
      return tableHead.reduce((acc, column) => {
        acc[column.field] = true;
        return acc;
      }, {});
    }
    return {};
  }, [JSON.stringify(tableHead?.map((col) => col.field))]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(columnInitialVisibility);
  ////....................COLUMN VISIBILITY SECTION......//
  return (
    <div>
      <DataGrid
        checkboxSelection={checkboxSelection}
        onSelectionModelChange={checkboxSelection ? handleSelectionChange : null}
        selectionModel={checkboxSelection ? checkedRowsIds : []}
        disableVirtualization
        components={{ Toolbar: CustomGridToolbar }}
        loading={loading}
        autoHeight
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            textOverflow: 'clip',
            whiteSpace: 'break-spaces',
            lineHeight: 1,
          },
        }}
        getRowHeight={getRowHeight}
        onRowDoubleClick={handleDubleClick}
        onRowClick={handleRowClick}
        rows={rows ? memoizedRows : []}
        columns={tableHead}
        pagination
        headerHeight={headerHeight || 56}
        paginationMode="server"
        getRowId={(row) => row[rowUniqueId]}
        pageSize={gridSize}
        columnBuffer={30}
        filterMode="server"
        rowCount={rowCount}
        rowsPerPageOptions={[5, 10, 25, 50]}
        disableSelectionOnClick={disableSelectionOnClick}
        onPageSizeChange={(newGridSize) => setGridSize(newGridSize)}
        page={pageNum - 1}
        onPageChange={(newPageNum) => {
          setPageNum(newPageNum + 1);
        }}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => {
          setColumnVisibilityModel(newModel);
        }}
      />
      <GlobalAnchor openAnchor={openAnchor} setOpenAnchor={setOpenAnchor} handleClick={handleClick}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}></Box>
        {anchorLoading ? (
          <Box
            sx={{
              width: 300,
              display: 'flex',
              justifyContent: 'center',
              pr: 7,
            }}
          >
            <Loader3D />
          </Box>
        ) : (
          <Box>{actionComponent}</Box>
        )}
      </GlobalAnchor>
    </div>
  );
};

export default GlobalDatagrid;
